import { QUESTIONS, TIERS, PILLAR_LABELS } from './constants';
import { PillarScores } from './types';
import { AssessmentContent } from './content/types';

export function calcScore(answers: (number | null)[]): number {
  const maxScore = QUESTIONS.length * 4;
  let raw = 0;
  answers.forEach((a, i) => {
    if (a !== null) raw += QUESTIONS[i].scores[a];
  });
  return Math.round((raw / maxScore) * 100);
}

export function calcPillarScores(answers: (number | null)[]): PillarScores {
  const keys: (keyof PillarScores)[] = ['strategy', 'arch', 'data', 'engVel', 'org'];
  const earned = [0, 0, 0, 0, 0];
  const max = [0, 0, 0, 0, 0];

  QUESTIONS.forEach((q, i) => {
    const p = q.pillar;
    max[p] += Math.max(...q.scores);
    if (answers[i] !== null) earned[p] += q.scores[answers[i] as number];
  });

  return keys.reduce((acc, key, i) => {
    acc[key] = max[i] > 0 ? Math.round((earned[i] / max[i]) * 100) : 0;
    return acc;
  }, {} as PillarScores);
}

/**
 * Canonical tier label, derived purely from the code's score boundaries.
 * Used for the HubSpot payload so the value stored in the CRM stays stable
 * regardless of the (editable) display labels in the CMS.
 */
export function getTier(score: number): string {
  return TIERS.find((t) => score >= t.min && score <= t.max)?.label ?? 'Early Stage';
}

/** Index of the matching tier in `TIERS`. Falls back to Early Stage (1). */
export function getTierIndex(score: number): number {
  const idx = TIERS.findIndex((t) => score >= t.min && score <= t.max);
  return idx === -1 ? 1 : idx;
}

/** Results summary for the score's tier, pulled from the resolved content. */
export function getTierSummary(score: number, content: AssessmentContent): string {
  return content.tierSummaries[getTierIndex(score)] ?? '';
}

/**
 * Strongest/weakest pillar insights. The ranking logic stays in code; the
 * pillar labels and insight bodies come from the resolved content (matched to
 * `PILLAR_LABELS` by position).
 */
export function getKeyInsights(
  pillar: PillarScores,
  content: AssessmentContent,
): Array<{ title: string; body: string }> {
  const entries = PILLAR_LABELS.map(({ key }, index) => ({
    index,
    label: content.pillars[index] ?? PILLAR_LABELS[index].label,
    pct: pillar[key],
  }));
  const sorted = [...entries].sort((a, b) => b.pct - a.pct);
  const strongest = sorted[0];
  const weakest = sorted[sorted.length - 1];

  return [
    {
      title: `${strongest.label} is your strongest pillar (${Math.round(strongest.pct)}%)`,
      body: content.insights[strongest.index]?.strong ?? '',
    },
    {
      title: `${weakest.label} is the gap to close (${Math.round(weakest.pct)}%)`,
      body: content.insights[weakest.index]?.weak ?? '',
    },
  ];
}
